import { useOrganization } from '_/contexts/OrganizationContext';
import useFlowOpen from '_/features/flows/common/hooks/useFlowOpen';
import { EFlowType, IFlowDefinitionChain } from '_/features/flows/common/types/flow.types';
import { useOrgCollections } from '_/hooks/useCollections';
import { Card } from 'antd';
import Checkbox from 'antd/lib/checkbox';
import Collapse from 'antd/lib/collapse';
import CollapsePanel from 'antd/lib/collapse/CollapsePanel';
import Space from 'antd/lib/space';
import Table from 'antd/lib/table';
import { ColumnsType } from 'antd/lib/table/interface';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import React, { ReactElement, useMemo } from 'react';

import classes from './FlowInfo.module.css';
import { IFlowInfoProps } from './FlowInfo.types';
import FlowInfoItem from './FlowInfoItem';
import FlowInfoValue from './FlowInfoValue';

const columns: ColumnsType<IFlowDefinitionChain> = [
  {
    dataIndex: 'name',
    title: 'Name',
  },
  {
    dataIndex: 'transform',
    title: 'JQ Transform',
  },
  {
    dataIndex: 'keyTransform',
    title: 'JQ Deduplication Transform',
  },
  {
    dataIndex: 'dataHours',
    title: 'Collect Data hrs.',
  },
  {
    dataIndex: 'chunkCollectHours',
    title: 'Chunk Collect hrs.',
  },
];

const FlowInfo = ({ flow, flowConfig = flow, flowDefinition = flow.definition }: IFlowInfoProps): ReactElement => {
  const { closeHours, dataHours, pushHours, skipQaTests, sourceCriteria } = flowConfig;
  const { id: flowId, type } = flow;
  const { chains = [], collectionId } = flowDefinition;
  const chained = chains.length > 0;

  const { selectedOrgSlug } = useOrganization();
  const { data: collections = [] } = useOrgCollections(selectedOrgSlug);
  const collectionsNames = useMemo(() => collections.reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {}), [collections]);

  const deliveryType = useMemo(() => (flowDefinition?.chains?.length ? EFlowType.CHAINED : type), [flowDefinition, type]);

  const openFlow = useFlowOpen();

  const dataSource = useMemo(
    () => chains.map((def) => ({ ...def, name: collectionsNames[def.collectionId] })),
    [collectionsNames, chains],
  );

  return (
    <Card>
      <Space className="wb-flexbox wb-flexColumn wb-fullWidth" direction="vertical">
        <Space className="wb-flexbox wb-flexColumn" direction="vertical">
          <Text className={classes.title} type="secondary">
            Id
          </Text>
          <div>
            <Tooltip title="Click to open the Flow">
              <span className={classes.link} onClick={openFlow}>
                <Text>{flowId}</Text>
              </span>
            </Tooltip>
          </div>
        </Space>
        <FlowInfoValue title="Type" value={deliveryType} />
        {!chained && <FlowInfoValue title="Collection" value={collectionsNames[collectionId]} />}
        <FlowInfoValue title="Input uri" value={flowDefinition.inputUriTemplate} />
        <FlowInfoValue title="Hours to collect data" value={dataHours} />
        <FlowInfoValue title="Hours from Start to Destination Push" value={pushHours} />
        <FlowInfoValue title="Hours To Finish" value={closeHours} />
        <FlowInfoValue title="Chunks" value={flowDefinition.chunks || 0} />
        {sourceCriteria && (
          <FlowInfoItem title="Source filters">
            {sourceCriteria.filters?.map(({ parameters }) =>
              Object.entries(parameters).map(([key, value]) => (
                <div key={key}>
                  {key}: {value}
                </div>
              )),
            )}
          </FlowInfoItem>
        )}
        <FlowInfoItem title="QA Checks">
          <div>
            Skip QA Checks: <Checkbox checked={skipQaTests} disabled />
          </div>
        </FlowInfoItem>
        {flowDefinition.inputTransform && <FlowInfoValue title="JQ Transform" value={flowDefinition.inputTransform} />}
        {flowDefinition.chainingParameter && (
          <FlowInfoValue title="Chaining Parameter" value={flowDefinition.chainingParameter} />
        )}
        {flowDefinition.aggregateChunks && (
          <FlowInfoItem title="Aggregate chunks">
            <div>
              <div>
                Aggregate chunks: <Checkbox checked={flowDefinition.aggregateChunks} disabled />
              </div>
              <div>
                Retain Original Chunk File Pushes: <Checkbox checked={flowDefinition.retainOriginalChunkFiles} disabled />
              </div>
              <div>
                Ignore Duplicate Rows: <Checkbox checked={flowDefinition.ignoreAggregationDupes} disabled />
              </div>
            </div>
          </FlowInfoItem>
        )}
        {chained && (
          <Collapse className="wb-flexbox wb-flexColumn">
            <CollapsePanel header="Collections" key={1}>
              <Table columns={columns} dataSource={dataSource} pagination={false} rowKey="collectionId" />
            </CollapsePanel>
          </Collapse>
        )}
      </Space>
    </Card>
  );
};

export default FlowInfo;
